import React, { useState, useEffect } from "react";
import logo from '../../assets/img/logo.webp';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, messaging } from "../../firebase.config";
import { Error } from "../Form";
import { Link, useNavigate } from "react-router-dom";
import API from "../../api/api";
import { onAuthStateChanged } from "firebase/auth";
import { getToken } from "firebase/messaging";
import AuthWelcomeShell from "./AuthWelcomeShell";
import shellStyles from "./AuthWelcomeShell.module.css";

interface Messages {
  message?: string
}

const SignIn: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Messages>({});
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthChecked(true);
      if (user) {
        if (user.emailVerified) {
          navigate("/dashboard");
        } else {
           setError({
             message:
               "Your account is not verified. Please check your email for verification instructions."
           });
            auth.signOut();
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (!isAuthChecked) {
    return (
      <div className={`${shellStyles.authTheme} ${shellStyles.loadingRoot}`}>
        <div className={`loader ${shellStyles.loadingInner}`}>Loading...</div>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError({});
    const { email, password } = formData;
    
    try {
      const response = await API.post('/auth/check-user', { email });
    
      if (!response.data.exists) {
        setError({
          message: "The account does not exist, Kindly register",
        });
        setLoading(false);
        return;
      }
      
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      
      if (user) {
        if (user?.emailVerified === false) {
          setError({
            message: "Your account is not verified. Please check your email for verification instructions."
          });
          setLoading(false);
          return;
        }
        const fcmToken = await getToken(messaging, {
          vapidKey: process.env.VITE_APP_FIREBASE_VAPID_KEY,
        });

        if (fcmToken) {
          const lastFcmToken = localStorage.getItem("lastFcmToken");
          if (fcmToken !== lastFcmToken) {
            await API.post(`/notifications/save-fcm-token/${user.uid}`, { fcmToken });
            localStorage.setItem("lastFcmToken", fcmToken);
          }
        } else {
          console.warn("No FCM token received.");
        }
        localStorage.setItem("token", JSON.stringify(await user.getIdToken()));
      } else {
        setLoading(false);
        setError({
          message: "Login failed. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Sign in error:", error);
      localStorage.removeItem("token");
      setLoading(false);
      if (error.code === "auth/invalid-credential") {
        setError({
          message: "Your Email or Password is incorrect, Please Try Again or Create new Password",
        });
      } else if (error.code === "auth/user-disabled") {
        setError({
          message:
            "Log in failed: Your account has been disabled. Please contact if you think this was a mistake",
        });
      } else if (error.code === "auth/user-not-found") {
        setError({
          message: "Log in failed: No account exists with this email address",
        });
      } else {
        setError({
          message: `Something went wrong: ${error.code}`,
        });
      }
    }
  };

  return (
    <AuthWelcomeShell pageLabel="Sign in">
      <div className={shellStyles.logoBlock}>
        <Link to="/" aria-label="Home">
          <img
            src={logo}
            alt="iGiTREE logo"
            className={shellStyles.logoImg}
          />
        </Link>
      </div>
      <h1 className={shellStyles.title}>Log in to iGiTREE</h1>
      <p className={shellStyles.subtitle}>
        Enter your credentials to continue to your dashboard.
      </p>

      <form onSubmit={handleSubmit} className="main-input">
        <div className="input-group">
          <span className="input-group-text">
            <i className="fa-sharp fa-light fa-envelope"></i>
          </span>
          <div className="form-floating">
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              required
              autoComplete="off"
            />
            <label>Email Address</label>
          </div>
        </div>

        <div style={{ display: 'none' }}>
          <input type="text" autoComplete="username" style={{ display: 'none' }} />
        </div>

        <div className="input-group">
          <span className="input-group-text">
            <i className="fa-sharp fa-light fa-lock"></i>
          </span>
          <div className="form-floating position-relative">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              required
              autoComplete="new-password"
            />
            <label>Password</label>
            <i 
              className={`fa-sharp fa-light ${showPassword ? 'fa-eye-slash' : 'fa-eye'} position-absolute`}
              onClick={() => setShowPassword(!showPassword)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowPassword(!showPassword); } }}
              role="button"
              tabIndex={0}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                zIndex: 5
              }}
            ></i>
          </div>
        </div>

        <button type="submit" className={shellStyles.btnGradient} disabled={loading}>
          {loading ? "Logging In..." : "Log In"}
        </button>
      </form>
      {error?.message && <Error>{error.message}</Error>}
      <div className={shellStyles.linkRow}>
        <Link to="/forgot" className={shellStyles.link}>
          I forgot my password
        </Link>
      </div>
      <div className={shellStyles.linkRow}>
        <span className={shellStyles.muted}>Don&apos;t have an account? </span>
        <Link to="/register" className={shellStyles.link}>
          Sign Up
        </Link>
      </div>
    </AuthWelcomeShell>
  );
};

export default SignIn;
